// Formatação e datas do cadastro de desconto v2. Funções puras.
//
// Data e hora do desconto são SEMPRE de Brasília (UTC-3): é o fuso em que o posto avalia
// o horário (pontuax-backend src/models/discount.ts validScheduledDiscount). Por isso datas
// viram texto "YYYY-MM-DD" e nenhum cálculo depende do fuso do navegador.
import { getAppLocale } from '../../../i18n/locale'

export const NBSP = ' '
/** Brasília não tem horário de verão desde 2019. */
export const BR_OFFSET = '-03:00'
const BR_OFFSET_MS = 3 * 3600 * 1000

export const pad = (n: number) => String(n).padStart(2, '0')

const intlTag = () => (getAppLocale() === 'es' ? 'es' : 'pt-BR')

// ─── Dinheiro e números ─────────────────────────────────────────────────────────

export const fmtCents = (c: number) => (c / 100).toFixed(2).replace('.', ',')
export const brl = (c: number) => `R$${NBSP}${fmtCents(c)}`
export const fmtN = (n: number) => Number(n).toLocaleString('pt-BR')
export const digits = (v: unknown, max: number) => String(v == null ? '' : v).replace(/\D/g, '').slice(0, max)

/** Máscara de centavos: digitar 20 vira 0,20 (R$ 0,20). */
export const centsFrom = (v: unknown): number | null => {
  const d = digits(v, 5)
  return d ? parseInt(d, 10) : null
}

export const toInt = (v: unknown) => parseInt(String(v), 10) || 0

// ─── Texto ──────────────────────────────────────────────────────────────────────

/** "A, B e C" / "A, B ou C" (em espanhol, "y" / "o"). */
export const joinList = (items: string[], kind: 'and' | 'or' = 'and') => {
  if (items.length <= 1) return items[0] || ''
  try {
    return new Intl.ListFormat(intlTag(), { style: 'long', type: kind === 'and' ? 'conjunction' : 'disjunction' }).format(items)
  } catch {
    return items.join(', ')
  }
}

export const norm = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export const lowerFirst = (t: string) => (t ? t[0].toLowerCase() + t.slice(1) : t)

/**
 * Corta no limite sem partir frase no meio; devolve o texto inteiro se couber. Prefere cortar entre blocos
 * (" · "), para o nome sugerido não acabar em "... · fim de"; sem bloco, corta na última palavra inteira.
 */
export const clip = (text: string, max: number) => {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const block = cut.lastIndexOf(' · ')
  const at = block > 0 ? block : cut.lastIndexOf(' ')
  return (at > max * 0.3 ? cut.slice(0, at) : cut).replace(/[\s·,;:-]+$/, '')
}

/** Texto cru do usuário vira HTML seguro para o corpo da notificação (o app renderiza HTML). */
export const toHtml = (text: string) => {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return String(text)
    .trim()
    .split(/\n{2,}/)
    .map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

// ─── Datas (YYYY-MM-DD) ─────────────────────────────────────────────────────────

export const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}
export const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const addDays = (s: string, n: number) => {
  const d = parseDay(s)
  d.setDate(d.getDate() + n)
  return isoDay(d)
}
export const dowOf = (s: string) => parseDay(s).getDay()
export const diffDays = (a: string, b: string) => Math.round((parseDay(b).getTime() - parseDay(a).getTime()) / 864e5)
export const isValidDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && isoDay(parseDay(s)) === s

/** Hoje, em Brasília, como YYYY-MM-DD. */
export const todayStr = (now: Date) => {
  const b = new Date(now.getTime() - BR_OFFSET_MS)
  return `${b.getUTCFullYear()}-${pad(b.getUTCMonth() + 1)}-${pad(b.getUTCDate())}`
}
/** Hora de Brasília (HH:mm) de um instante. */
export const clockStr = (date: Date) => {
  const b = new Date(date.getTime() - BR_OFFSET_MS)
  return `${pad(b.getUTCHours())}:${pad(b.getUTCMinutes())}`
}
/** YYYY-MM-DDTHH:mm de Brasília para um instante (valor de input datetime-local). */
export const localStr = (date: Date) => `${todayStr(date)}T${clockStr(date)}`

/** "YYYY-MM-DDTHH:mm" (Brasília) -> instante. */
export const brDate = (local: string) => new Date(`${local.length === 16 ? `${local}:00` : local}${BR_OFFSET}`)
/** Data + "HH:mm" -> ISO com -03:00 (é o que o backend espera). */
export const isoAt = (day: string, hhmm: string, seconds = '00') => `${day}T${hhmm}:${seconds}${BR_OFFSET}`

/** HH:mm de Brasília de um ISO qualquer (clone de desconto que já existe). */
export const hhmmOf = (iso: string | null | undefined) => {
  if (!iso) return ''
  const t = Date.parse(iso)
  return Number.isNaN(t) ? '' : clockStr(new Date(t))
}
/** YYYY-MM-DD de Brasília de um ISO qualquer. */
export const dayOf = (iso: string | null | undefined) => {
  if (!iso) return ''
  const t = Date.parse(iso)
  return Number.isNaN(t) ? '' : todayStr(new Date(t))
}

/** "sáb", "dom" ... (3 letras, minúsculas, no idioma da tela). */
export const dowShort = (i: number) => {
  const f = new Intl.DateTimeFormat(intlTag(), { weekday: 'short', timeZone: 'UTC' })
  return f.format(new Date(Date.UTC(2000, 0, 2 + i))).replace(/\.$/, '').toLowerCase()
}
export const monthName = (m: number) =>
  new Intl.DateTimeFormat(intlTag(), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, m - 1, 1)))

export const dayShort = (s: string) => {
  const d = parseDay(s)
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
}
/** "sáb, 17/10" */
export const dayLabel = (s: string) => `${dowShort(dowOf(s))}, ${dayShort(s)}`
